import { useCallback, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel, GridSlots } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import ClientModel from "../../core/api/clients/ClientModel";
import { useAuth } from "../../hooks/AuthProvider";
import { useNotifications } from "../../hooks/NotificationProvider";
import { confirmDialog } from "../common/ConfirmDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import qs from "qs";
import { translations } from "../../translations/common";
import { 
  RootContainer, 
  FullRow, 
  BoldRow, 
  BoldText, 
  FixedWidthText, 
  RightText 
} from "./styles/ClientTable.styles";
import { tableContainerStyles, dataGridStyles } from "../common/styles/TableStyles";

interface Props {
  clients: Array<ClientModel>;
  refresh(): void;
  isLoading: boolean;
  pageSize: number;
  customHeader?: React.ReactNode;
  slots?: Partial<GridSlots>;
}

export default function ClientTable({
  clients,
  refresh,
  isLoading,
  pageSize,
  customHeader,
  slots,
}: Props) {
  const auth = useAuth();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize,
    page: 0,
  });
  
  // Ensure each row has an id for DataGrid
  const rowsWithIds = clients.map(client => ({
    ...client,
    id: client.id || `temp-${client.companyCode}-${client.companyTitle}`,
  }));

  const myClients = new URLSearchParams(location.search).get("myClients") === "true";

  const handleView = useCallback((id: string) => {
    navigate({
      pathname: `/client/${id}`,
      search: qs.stringify({ myClients }),
    });
  }, [navigate, myClients]);

  const handleDelete = useCallback((rowData: ClientModel) => {
    confirmDialog({
      title: translations.clientTable.actions.delete,
      body: translations.clientTable.actions.deleteConfirm(rowData.companyTitle),
      action: async () => {
        try {
          if(rowData.id) {
            await ClientModel.delete(rowData.id);
          }
          refresh();
          notifications.notify({
            level: "success",
            message: translations.clientTable.actions.deleteSuccess,
          });
        } catch (err: any) {
          notifications.notify({
            level: "error",
            message: err.response?.data?.description,
          });
        }
      },
      actionName: translations.clientTable.actions.delete,
    });
  }, [refresh, notifications]);

  const renderCompanyInfo = useCallback((params: GridRenderCellParams) => (
    <RootContainer>
      <FullRow>
        <BoldText>{translations.clientTable.labels.company}:&nbsp;</BoldText>
        <FixedWidthText>{params.row.companyTitle}</FixedWidthText>
      </FullRow>
      <FullRow>
        <BoldText>{translations.clientTable.labels.companyCode}:&nbsp;</BoldText>
        <RightText>{params.row.companyCode}</RightText>
      </FullRow>
      <FullRow>
        <BoldText>{translations.clientTable.labels.vatCode}:&nbsp;</BoldText>
        <RightText>{params.row.companyVatCode}</RightText>
      </FullRow>
    </RootContainer>
  ), []);

  const renderContactInfo = useCallback((params: GridRenderCellParams) => (
    <RootContainer>
      <FullRow>
        <BoldText>{translations.clientTable.labels.address}:&nbsp;</BoldText>
        <FixedWidthText>{params.row.companyAddress}</FixedWidthText>
      </FullRow>
      <FullRow>
        <BoldText>{translations.clientTable.labels.phone}:&nbsp;</BoldText>
        <RightText>{params.row.companyPhone}</RightText>
      </FullRow>
      <FullRow>
        <BoldText>{translations.clientTable.labels.email}:&nbsp;</BoldText>
        <RightText>{params.row.mainEmail}</RightText>
      </FullRow>
    </RootContainer>
  ), []);

  const renderClientState = useCallback((params: GridRenderCellParams) => (
    <RootContainer>
      <FullRow>
        <BoldText>{translations.clientTable.labels.validFrom}:&nbsp;</BoldText>
        <RightText>{params.row.validFrom}</RightText>
      </FullRow>
      <FullRow>
        <BoldText>{translations.clientTable.labels.validTo}:&nbsp;</BoldText>
        <RightText>{params.row.validTo}</RightText>
      </FullRow>
      <FullRow>
        <BoldText>{translations.clientTable.labels.state}:&nbsp;</BoldText>
        <RightText>
          {translations.clientTable.states[params.row.clientState as keyof typeof translations.clientTable.states]}
        </RightText>
      </FullRow>
    </RootContainer>
  ), []);

  const renderResponsiblePerson = useCallback((params: GridRenderCellParams) => (
    <RootContainer>
      <BoldRow>{translations.clientTable.columns.responsiblePerson}</BoldRow>
      <FullRow>{params.row.responsibleUserName}</FullRow>
    </RootContainer>
  ), []);

  const renderActions = useCallback((params: GridRenderCellParams<any, ClientModel & { id: string }>) => {
    const { id } = params.row;
    return (
      <Box>
        <Tooltip title={translations.clientTable.actions.view}>
          <IconButton onClick={() => handleView(id)} size="small">
            <PersonIcon />
          </IconButton>
        </Tooltip>
        {auth.isAdmin && (
          <Tooltip title={translations.clientTable.actions.delete}>
            <IconButton onClick={() => handleDelete(params.row)} size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }, [auth.isAdmin, handleDelete, handleView]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: translations.clientTable.columns.id,
      width: 70,
    },
    {
      field: "companyInfo",
      headerName: translations.clientTable.columns.companyInfo,
      flex: 1,
      renderCell: renderCompanyInfo,
    },
    {
      field: "contactInfo",
      headerName: translations.clientTable.columns.contactInfo,
      flex: 1,
      renderCell: renderContactInfo,
    },
    {
      field: "clientState",
      headerName: translations.clientTable.columns.clientState,
      flex: 1,
      renderCell: renderClientState,
    },
    {
      field: "responsiblePerson",
      headerName: translations.clientTable.columns.responsiblePerson,
      flex: 1,
      renderCell: renderResponsiblePerson,
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: renderActions,
    },
  ];

  return (
    <Box sx={{ ...tableContainerStyles }}>
      {customHeader}
      <DataGrid
        rows={rowsWithIds}
        columns={columns.map(col => ({ ...col, sortable: false }))}
        loading={isLoading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[pageSize]}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        rowHeight={80}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {translations.clientTable.noData}
            </Box>
          ),
          ...slots,
        }}
        sx={{
          ...dataGridStyles,
          maxHeight: '80vh',
        }}
      />
    </Box>
  );
}
